/**
 * App Component
 */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import 'moment/min/locales.min';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { ReportNavigatorService } from '@containers/reports/report/services/report-navigator.service';
import { init_app } from '@rpt-ngrx/actions/ui.action';
import { CacheService } from '@services/cache';
import { HttpCancelService } from '@services/http-client';
import { PostMessageService } from '@services/post-message';

/**
 * App Component
 * Top Level Component
 */
@Component({
    selector: 'app-root',
    templateUrl: './app.template.html',
    styleUrls: ['./app.style.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
    private portalMenuVisibility = false;
    private dispose$ = new Subject();

    /**
     * Initialize the component
     * Components need initialization in the constructor.
     */
    constructor(
        private router: Router,
        private postMessageService: PostMessageService,
        private httpCancelService: HttpCancelService,
        private store: Store,
        private cacheService: CacheService,
        private reportNavigator: ReportNavigatorService,
        private translations: TranslateService,
    ) {}

    private clearSessionStorage(): void {
        sessionStorage.clear();
    }

    /**
     * This function is called right after the directive's
     * data-bound properties have been checked for the first time,
     * and before any of its children have been checked.
     * It is invoked only once when the directive is instantiated.
     * In this function we listen to route state. And once current url
     * isn't /login then we check if customer is logged in. And if
     * customer isn't logged in then we do navigate to login page.
     * Also in this function we select needed sidebar menu item
     * to update title of the sidebar link
     */

    public ngOnInit() {
        this.cacheService.clearHttpCache();
        this.store.dispatch(init_app());
        this.router.events
            .pipe(
                takeUntil(this.dispose$),
                filter((event) => event instanceof NavigationStart),
            )
            .subscribe((event: NavigationStart) => {
                if (event.restoredState && event.url === '/custom-reports') {
                    this.reportNavigator.unloadReport();
                }
            });
        this.clearSessionStorage();
    }

    ngOnDestroy(): void {
        this.dispose$.next();
        this.dispose$.complete();
    }

    @HostListener('document:click', ['$event.target'])
    public dispatchClickToParentIframe() {
        if (this.portalMenuVisibility) {
            parent.postMessage('hidePortalSubMenu', '*');
        }
    }

    @HostListener('window:message', ['$event'])
    public iframeMessageListener(event: MessageEvent): void {
        if (event && event.data) {
            try {
                const val = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                this.portalMenuVisibility = _.get(val, 'portalSubMenuVisibility', false);
                this.postMessageService.processPostMessage(val);
            } catch {
                return;
            }
        }
    }

    @HostListener('window:beforeunload', ['$event'])
    clearLocalStorage() {
        this.cacheService.clearHttpCache();
    }
}
